// ExamEnvironment.js
// Composes all modular exam environment components for use in a route or modal
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Stack,
  Chip
} from '@mui/material';
import ExamLayout from '../examEnv/ExamLayout';
import ExamHeader from '../examEnv/ExamHeader';
import ExamControls from '../examEnv/ExamControls';
import InstructionsModal from '../examEnv/InstructionsModal';
import SecurityLayer from '../examEnv/SecurityLayer';
import { useExam } from '../examEnv/ExamContext';

const ExamEnvironment = ({ examInfo, candidate, questions, initialTimer, onClose }) => {
  // Compose initial exam state for context
  const initialExam = {
    examInfo,
    candidate,
    questions,
    timer: initialTimer,
    status: 'in-progress',
  };
  const [instructionsOpen, setInstructionsOpen] = useState(true);

  const handleStartExam = () => setInstructionsOpen(false);
  const handleCloseExam = () => {
    if (onClose) onClose();
  };

  return (
    <ExamLayout initialExam={initialExam}>
      <SecurityLayer />
      <InstructionsModal open={instructionsOpen} onStart={handleStartExam} onClose={handleCloseExam} />
      {/* AccessibilityTools, Calculator, and NotesPad removed as per requirements */}
      {!instructionsOpen && <>
        <ExamHeader onClose={handleCloseExam} />
        <AllQuestionsPanel />
        <ExamControls />
      </>}
    </ExamLayout>
  );
}

// Renders all questions at once with MCQ options
function AllQuestionsPanel() {
  const { state, dispatch } = useExam();
  const { questions, answers } = state;
  
  if (!questions || questions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No questions found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, flex: 1, overflow: 'auto' }}>
      <Stack spacing={4}>
        {questions.map((q, idx) => {
          const answerObj = answers[q.id] || {};
          
          const getOptionDetails = (opt) => {
            if (typeof opt === 'object' && opt !== null) {
              return {
                text: opt.text || '',
                imageUrl: opt.imageUrl || null
              };
            }
            return {
              text: String(opt || ''),
              imageUrl: null
            };
          };

          const getOptionLabel = (opt, index) => {
            const labels = ['A', 'B', 'C', 'D', 'E'];
            const { text } = getOptionDetails(opt);
            return `${labels[index] || index + 1}. ${text}`;
          };

          const matchOption = (opt1, opt2) => {
            if (!opt1 || !opt2) return false;
            const d1 = getOptionDetails(opt1);
            const d2 = getOptionDetails(opt2);
            return d1.text === d2.text && d1.imageUrl === d2.imageUrl;
          };

          const hasImageOptions = q.options && q.options.some(opt => typeof opt === 'object' && opt !== null && opt.imageUrl);

          const handleNumericChange = (e) => {
            dispatch({ type: 'SET_ANSWER', qid: q.id, payload: { ...answerObj, answer: e.target.value } });
          };

          return (
            <Card 
              key={q.id} 
              elevation={1}
              sx={{ 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Question Header */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Chip
                    label={`Q ${idx + 1}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  {answerObj.answer && (
                    <Chip
                      label="Answered"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {/* Question Text */}
                <Typography 
                  variant="h6" 
                  component="div"
                  sx={{ 
                    mb: 3,
                    lineHeight: 1.6,
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  {q.text}
                </Typography>

                {/* Question Image */}
                {q.image && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img 
                      src={q.image} 
                      alt="Question illustration" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 300,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }} 
                    />
                  </Box>
                )}

                {/* Answer Options */}
                <Box sx={{ mt: 3 }}>
                  {q.type === 'mcq' && q.options && (
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        name={`question_${q.id}`}
                        value={answerObj.answer ? (typeof answerObj.answer === 'string' ? answerObj.answer : answerObj.answer.text) : ''}
                      >
                        <Box 
                          sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: hasImageOptions ? { xs: '1fr', md: '1fr 1fr' } : '1fr', 
                            gap: 2 
                          }}
                        >
                          {q.options.map((opt, oidx) => {
                            const isSelected = matchOption(answerObj.answer, opt);
                            const { text, imageUrl } = getOptionDetails(opt);
                            return (
                              <Card
                                key={oidx}
                                variant="outlined"
                                sx={{
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    borderColor: 'secondary.main',
                                    bgcolor: (theme) => `${theme.palette.secondary.main}08`
                                  },
                                  ...(isSelected && {
                                    borderColor: 'secondary.main',
                                    bgcolor: (theme) => `${theme.palette.secondary.main}15`,
                                    borderWidth: 2
                                  })
                                }}
                                onClick={() => dispatch({ type: 'SET_ANSWER', qid: q.id, payload: { ...answerObj, answer: opt } })}
                              >
                                <FormControlLabel
                                  value={typeof opt === 'string' ? opt : text}
                                  control={<Radio checked={isSelected} sx={{ color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }} />}
                                  label={
                                    <Box sx={{ width: '100%', py: 1 }}>
                                      <Typography 
                                        variant="body1" 
                                        sx={{ 
                                          fontWeight: isSelected ? 600 : 400,
                                          color: isSelected ? 'secondary.main' : 'text.primary',
                                          whiteSpace: 'pre-wrap'
                                        }}
                                      >
                                        {getOptionLabel(opt, oidx)}
                                      </Typography>
                                      {imageUrl && (
                                        <Box 
                                          sx={{ 
                                            mt: 1.5, 
                                            textAlign: 'center', 
                                            overflow: 'hidden', 
                                            borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            p: 1,
                                            bgcolor: 'background.paper'
                                          }}
                                        >
                                          <Box 
                                            component="img" 
                                            src={imageUrl} 
                                            alt={text}
                                            sx={{ 
                                              maxHeight: 180, 
                                              maxWidth: '100%', 
                                              objectFit: 'contain',
                                              transition: 'transform 0.25s ease-in-out',
                                              cursor: 'zoom-in',
                                              '&:hover': {
                                                transform: 'scale(1.05)'
                                              }
                                            }} 
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                  sx={{ 
                                    width: '100%',
                                    m: 0,
                                    px: 2,
                                    py: 1,
                                    alignItems: imageUrl ? 'flex-start' : 'center'
                                  }}
                                />
                              </Card>
                            );
                          })}
                        </Box>
                      </RadioGroup>
                    </FormControl>
                  )}

                  {q.type === 'numeric' && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                        Enter your numerical answer:
                      </Typography>
                      <TextField
                        type="number"
                        value={answerObj.answer || ''}
                        onChange={handleNumericChange}
                        placeholder="Enter your answer"
                        variant="outlined"
                        sx={{ 
                          width: 200,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            fontSize: '1.1rem'
                          }
                        }}
                        inputProps={{
                          style: { textAlign: 'center', fontWeight: 600 }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

export default ExamEnvironment;
